import { useCallback, useRef } from 'react'

import { User } from '@sentry/types'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'

import config from '@config'
import { captureExceptionWithUser } from '@utils/sentry'
import useAuthentication from '@hooks/useAuthentication'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import { ContentAccessStatus } from '@hooks/useGetContentAccessStatus'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { updateTiunSnippet } from '@components/Tiun/utils'

import {
  LoginFromPiano,
  NonSiteActionFn,
  OnCheckoutCloseParams,
  OnCheckoutCompleteFn,
  OnCompleteUpgradePurchaseParams,
  OneLogLoginCases,
  OnExperienceExecuteParams,
  OnSetResponseVariableParams,
  OnShowOfferParams,
  PianoCheckoutParams,
  PianoEvents,
  PianoTrackingEvent,
  SubmitPaymentParams,
} from './types'
import {
  doParseStringifiedData,
  enableSubscriptionAutoRenewal,
  getElementTrackingParam,
  getTrackingCheckoutParams,
  getTrackingParams,
  setExperienceCookie,
  setIsArticleFreeToReadCookie,
  setTrackingCheckoutParams,
} from './utils'

const {
  auth: { ssoSourceSuffix },
  piano: { blickPlusOfferCardName, regwallTemplateCardName, applicationId },
  subscriptions: {
    conversionUrl,
    footerBannerClassname,
    restrictedDrawerClassname,
  },
} = config

const usePianoEvents = (): PianoEvents => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const oneLogLoginCases = useRef<OneLogLoginCases>({})

  const { loginWithSource } = useAuthentication()

  const trackPianoEvent = useCallback<TrackingFnType<PianoTrackingEvent>>(
    ({ extraData }) => getTrackingParams(extraData),
    [],
  )

  const trackingFunction = useTracking<PianoTrackingEvent>(trackPianoEvent)

  const doTrackPianoEvent = useCallback(
    (trackingEvent: PianoTrackingEvent) => {
      if (trackingEvent?.type && !trackingEvent.exception) {
        trackingFunction(trackingEvent)
      }
    },
    [trackingFunction],
  )

  const trackUnlockRestrictedContent = useCallback<
    TrackingFnType<{ paywall_bypass: string }>
  >(
    ({ extraData }) => ({
      event: 'unlock_content',
      ...extraData,
    }),
    [],
  )

  const handleTrackUnlockRestrictedContent = useTracking(
    trackUnlockRestrictedContent,
  )

  const loginFromPiano: LoginFromPiano = (
    params,
    loginCase,
    isCheckout = false,
  ) => {
    // Only set a cookie, if it's a click that happened on a startCheckout button in the Piano template
    // Otherwise an offer could be shown for Logins, where we don't want to show the user an offer
    if (isCheckout) {
      setExperienceCookie(params)
    }

    const { offerId, templateId, templateVariantId, experienceId } = params

    // Gets the source from pianos custom variable, set on every page change
    // and appends the piano specific event data
    const source = `${
      window?.tp?.customVariables?.sourceSSO
    }_${offerId}_${templateId}${
      templateVariantId ? `_${templateVariantId}` : '_MAIN' // Tracking team requested to hardcode a value in case variant is undefined
    }_${experienceId}_${ssoSourceSuffix}`

    loginWithSource({
      source,
      loginCase: loginCase || 'email_only',
    })
  }

  const onLoginRequired = (params: any) => {
    // A OneLog login case can be different depending on the term or the experience
    // This login case can be defined in the Piano response variables
    // It will either take login case defined per term (login_case_termId), per experience (login_case) or fall back to
    // the default Piano login case (blickplus_piano)
    const loginCase =
      oneLogLoginCases.current[`login_case_${params.termId}`] ??
      oneLogLoginCases.current.login_case ??
      'blickplus_piano'

    loginFromPiano(params, loginCase, true)
  }

  const onCheckoutCustom = (event: any) => {
    const { eventName } = event
    const { params, buttontext, ...rest } = event?.params
    const parsedParams = doParseStringifiedData(params)
    const userData = queryClient.getQueryData<User>(['user'])

    const mergedParams = {
      ...(parsedParams ?? {}), // data about Piano experience metadata
      ...rest, // external events in Piano template
    }

    switch (eventName) {
      case 'piano_click_offer':
        // When an offer click happens, always save the latest checkout params
        setTrackingCheckoutParams(queryClient, mergedParams)

      case 'piano_click':
        if (rest.clickelement === 'login' && !userData) {
          loginFromPiano(parsedParams, rest.logincase, false)
        }

        doTrackPianoEvent({
          type: eventName,
          params: { ...mergedParams, buttontext },
        })

        break

      case 'tiun_click':
        setTrackingCheckoutParams(queryClient, mergedParams)
        doTrackPianoEvent({
          type: 'piano_click_offer',
          params: mergedParams,
        })
        updateTiunSnippet(queryClient, true)
        break

      case 'adblock_interaction':
        doTrackPianoEvent({
          type: eventName,
          params: { ...mergedParams, eventCategory: 'adblock' },
        })

        break

      case 'piano_click_close':
        doTrackPianoEvent({
          type: eventName,
          params: mergedParams,
        })

        break

      case 'enable_autorenewal':
        enableSubscriptionAutoRenewal(queryClient, mergedParams)

        doTrackPianoEvent({
          type: 'piano_click',
          params: mergedParams,
        })

        break

      case 'show_free_article': {
        const needsLogin = mergedParams?.needslogin === 'true'
        const shouldReload = !needsLogin

        setIsArticleFreeToReadCookie(router, mergedParams?.url, shouldReload)

        doTrackPianoEvent({
          type: 'piano_click',
          params: { ...mergedParams, buttontext },
        })

        if (needsLogin) {
          loginFromPiano(parsedParams, rest.logincase, false)
        }

        break
      }
    }
  }

  const setContentAccessStatus = useCallback(
    (contentAccessStatus: ContentAccessStatus) => {
      queryClient.setQueryData(['content-access-status'], contentAccessStatus)
      queryClient.invalidateQueries({
        queryKey: ['content-access-status'],
        exact: true,
      })
    },
    [queryClient],
  )

  const onShowOffer = (params: OnShowOfferParams) => {
    handleTracking(params)
  }

  const handleTracking = (params: OnShowOfferParams) => {
    const { displayMode } = params
    const shouldShowTiunSnippet = queryClient.getQueryData<boolean>([
      'show-tiun-snippet',
    ])
    if (
      !shouldShowTiunSnippet &&
      (displayMode === 'modal' ||
        params.containerSelector === `.${footerBannerClassname}` ||
        params.containerSelector === `.${restrictedDrawerClassname}`)
    ) {
      doTrackPianoEvent({ type: 'piano_show', params })
    } else {
      // Impression tracking for templates that should not immediately be tracked
      const pianoTemplateParams = queryClient.getQueryData<OnShowOfferParams[]>(
        ['pianoTemplateParams'],
      )

      // As there can be multiple templates on one page and the impression tracking will be called
      // when the template comes into the viewport, we need to save the template params in a template params array
      const pianoTemplateParamsArray = pianoTemplateParams || []
      pianoTemplateParamsArray.push(params)

      queryClient.setQueryData(
        ['pianoTemplateParams'],
        pianoTemplateParamsArray,
      )
      queryClient.invalidateQueries({
        queryKey: ['pianoTemplateParams'],
        exact: true,
      })
    }
  }

  const onShowTemplate = (params: OnShowOfferParams) => {
    handleTracking(params)
  }

  const onExperienceExecute = (params: OnExperienceExecuteParams) => {
    const experienceEvents = params.result.events

    const isPaywallTemplateShown = experienceEvents.some(
      (experienceEvent) =>
        experienceEvent.eventType === 'showOffer' &&
        experienceEvent.eventModuleParams?.moduleName ===
          blickPlusOfferCardName,
    )
    const isRegwallTemplateShown = experienceEvents.some(
      (experienceEvent) =>
        experienceEvent.eventType === 'showTemplate' &&
        experienceEvent.eventModuleParams?.moduleName ===
          regwallTemplateCardName,
    )

    // Article content should be blocked (black background, blocked scrolling), when the restricted drawer is used
    // When an experience injects at least one template into the pianoRestrictedDrawer div the purpose of it is to
    // be blocked
    const contentBlocked = experienceEvents.some(
      (exp) =>
        exp.eventParams?.containerSelector === `.${restrictedDrawerClassname}`,
    )

    // Article content should be hidden, when the specific card name is set on a `showOffer` or `showTemplate` card
    const contentHidden = isPaywallTemplateShown || isRegwallTemplateShown

    setContentAccessStatus({ contentHidden, contentBlocked })
  }

  const onCustomEventTracking: NonSiteActionFn = (
    _eventParams,
    cardParams,
    contextParams,
  ) => {
    //@ts-expect-error
    const cardNameChunks = cardParams.moduleName.split(/_(.*)/s)
    if (!cardNameChunks?.length) {
      console.error(
        `[Piano tracking] Invalid event name for nonSite action: ${cardParams.moduleName}`,
      )
      return
    }
    // Event name is whatever is after the first "_" in the card name
    const [eventPrefix, eventName] = cardNameChunks
    if (eventPrefix === 'track' && eventName) {
      const { experienceId } = contextParams
      doTrackPianoEvent({ type: eventName, params: { experienceId } })
    }
  }

  const onSubmitPayment = (params: SubmitPaymentParams) => {
    const {
      term: { chargeCurrency, chargeAmount, billingPlanTable },
    } = params

    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient,
    ) as PianoCheckoutParams

    const trackingRelevantParams = {
      ...previousPianoCheckoutParams,
      variant: billingPlanTable[0]?.period,
      price: chargeAmount,
      currency: chargeCurrency,
    }

    // Update the checkout params with variant, price and currency
    setTrackingCheckoutParams(queryClient, trackingRelevantParams)

    doTrackPianoEvent({
      type: 'purchase_attempt',
      params: trackingRelevantParams,
      exception: previousPianoCheckoutParams.clickelement === 'registration',
    })
  }

  const onCompleteUpgradePurchase = ({
    termConversionId,
  }: OnCompleteUpgradePurchaseParams) => {
    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient,
    ) as PianoCheckoutParams

    doTrackPianoEvent({
      type: 'purchase_success',
      params: {
        ...previousPianoCheckoutParams,
        termConversionId,
        price: previousPianoCheckoutParams.adjustedprice,
      },
    })
  }

  const onCheckoutComplete: OnCheckoutCompleteFn = async (conversionObj) => {
    const { paymentId, termConversionId } = conversionObj

    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient,
    ) as PianoCheckoutParams

    // On registration conversions we need to ignore the currency, price and the transactionId
    if (conversionObj?.type === 'registration') {
      if (previousPianoCheckoutParams) {
        const rest = { ...previousPianoCheckoutParams }
        delete rest.currency
        delete rest.price

        doTrackPianoEvent({
          type: 'registration_success',
          params: {
            ...rest,
            termConversionId,
          },
        })
      }
    }
    // On default checkout conversions we need to supply tracking with the below parameters
    else {
      doTrackPianoEvent({
        type: 'purchase_success',
        params: {
          ...previousPianoCheckoutParams,
          transactionId: paymentId,
          termConversionId,
        },
      })
    }

    const payload = { aid: applicationId, ...conversionObj }
    const response = await fetch(conversionUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      captureExceptionWithUser(
        `[SUB. CHECKER ERROR] POST conversion returned an error: ${response.statusText}`,
        queryClient,
      )
    }
  }

  const onCheckoutClose = (event: OnCheckoutCloseParams) => {
    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient,
    ) as PianoCheckoutParams

    // Piano sends the checkout close event also on template events (e.g. footer banner)
    // We only want to track the checkout_close if it's really from the checkout template
    // The termId is only available after a user started the checkout
    if (previousPianoCheckoutParams.termid) {
      doTrackPianoEvent({
        type: 'checkout_close',
        params: {
          ...previousPianoCheckoutParams,
          state: event.state,
        },
        exception: previousPianoCheckoutParams.clickelement === 'registration',
      })

      // Reset checkout parameters when checkout is closed
      setTrackingCheckoutParams(queryClient, null)
    }

    // Default behavior is to refresh the page on successful checkout
    if (event.state === 'checkoutCompleted') {
      location?.reload?.()
    }
  }

  const onCheckoutSelectTerm = () => {
    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient,
    ) as PianoCheckoutParams

    const trackingRelevantParams = {
      ...previousPianoCheckoutParams,
      clickelement: getElementTrackingParam(
        previousPianoCheckoutParams.clickelement,
      ),
    }

    // Add correct clickelement to the tracking checkout params
    setTrackingCheckoutParams(queryClient, trackingRelevantParams)

    doTrackPianoEvent({
      type: 'start_checkout',
      params: trackingRelevantParams,
      exception: previousPianoCheckoutParams.clickelement === 'registration',
    })
  }

  const onSetResponseVariable = ({
    responseVariables,
  }: OnSetResponseVariableParams) => {
    oneLogLoginCases.current = responseVariables

    const paywall_bypass = responseVariables['paywall_bypass']
    if (paywall_bypass) {
      handleTrackUnlockRestrictedContent({ paywall_bypass })
    }
  }

  // As we have an SPA and Piano doesn't clean up the response variables,
  // we have to do this by ourselves, whenever a page change happens
  const resetResponseVariables = useCallback(() => {
    oneLogLoginCases.current = {}
  }, [])

  useExecuteOnClientNavigation(resetResponseVariables)

  return {
    onCheckoutClose,
    onCheckoutCustom,
    onShowOffer,
    onShowTemplate,
    onCustomEventTracking,
    onLoginRequired,
    onSubmitPayment,
    onExperienceExecute,
    onCheckoutComplete,
    onCheckoutSelectTerm,
    onCompleteUpgradePurchase,
    onSetResponseVariable,
  }
}

export default usePianoEvents
